import { ContentInsertVariables } from 'api/graphQL/types/contentInsert.types'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import React, { useEffect, useMemo } from 'react'
import { useHtmlPasteMutationHandler } from '../hooks/useHtmlPasteMutationHandler'
import { useInsertStatusPoller } from '../hooks/useInsertStatusPoller'
import { usePasteProgressDialogState } from '../hooks/usePasteProgressDialogState'

interface HtmlParagraphPasteProgressDialogProps {
    /** Variables for the content-insert mutation. */
    contentInsertVariables: Partial<ContentInsertVariables>
}

/** Dialog that shows the progress of an HTML paragraph paste
 * and calls a callback when the paste is completed.
 */
const HtmlParagraphPasteProgressDialog = ({
    contentInsertVariables,
}: HtmlParagraphPasteProgressDialogProps) => {
    const {
        instanceId,
        setInstanceId,
        progress,
        setProgress,
        statusText,
        setStatusText,
        isPasteInProgress,
        setIsPasteInProgress,
        warningText,
        setWarningText,
        isWarningIgnored,
        setIsWarningIgnored,
        handleCancel,
        handleProceed,
    } = usePasteProgressDialogState()

    const { handlePasteCompleted, handlePasteError, executePasteMutation } =
        useHtmlPasteMutationHandler({
            contentInsertVariables,
            setInstanceId,
            setWarningText,
            setIsPasteInProgress,
            isWarningIgnored,
        })

    useInsertStatusPoller({
        contentInsertVariables,
        instanceId,
        isPasteInProgress,
        progress,
        setProgress,
        setStatusText,
        setIsPasteInProgress,
        onComplete: handlePasteCompleted,
        onError: handlePasteError,
    })

    const confirmationDialogContent = useMemo(() => <>{warningText}</>, [
        warningText,
    ])

    useEffect(() => {
        if (contentInsertVariables && !instanceId && !isPasteInProgress) {
            executePasteMutation()
        }
    }, [
        contentInsertVariables,
        instanceId,
        isPasteInProgress,
        executePasteMutation,
    ])

    return warningText ? (
        <ConfirmationDialog
            dialogHeading="Warning!"
            confirmMessage="Are you sure you want to proceed with this paste?"
            confirmText="Yes"
            openDialog={!!warningText}
            handleDialogClose={handleCancel}
            dialogActionCallback={handleProceed}
            contentNode={confirmationDialogContent}
            size="small"
        />
    ) : (
        <ProgressDialog
            dialogId="html-para-paste-dialog"
            dialogName="Paste in Progress"
            open={true}
            progressPercentage={progress > 100 ? 100 : progress}
            statusText={statusText}
        />
    )
}

export default HtmlParagraphPasteProgressDialog
